class CertificationDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CertificationDrawer-Overlay').addEventListener('click', this.close.bind(this));
    
    // Initialize click handlers for certification links
    this.initializeCertificationLinks();
    
    // Watch for dynamically added certification links
    this.observeCertificationLinks();
  }

  initializeCertificationLinks() {
    // Find all certification links and add click handlers
    const certificationLinks = document.querySelectorAll('[data-certification-link]');
    
    certificationLinks.forEach(link => {
      // Skip if already initialized
      if (link.dataset.certificationLinkInitialized === 'true') {
        return;
      }
      
      link.dataset.certificationLinkInitialized = 'true';
      
      link.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Get logo from the rendered image element in the same certification item
        const certificationItem = link.closest('[data-certification-item]');
        let logo = null;
        if (certificationItem) {
          // Try multiple selectors to find the image
          const logoContainer = certificationItem.querySelector('.product-certifications__logo');
          if (logoContainer) {
            const logoImage = logoContainer.querySelector('img');
            if (logoImage) {
              logo = logoImage.src || logoImage.getAttribute('src');
            }
          }
        }
        
        // Fallback to data attribute if image not found
        if (!logo) {
          logo = link.getAttribute('data-certification-logo');
        }
        
        const title = link.getAttribute('data-certification-title');
        const information = link.getAttribute('data-certification-information');
        
        if (title && information) {
          this.open(link, {
            logo: logo || null,
            title: title,
            more_information: information
          });
        }
      });
    });
  }

  observeCertificationLinks() {
    const observer = new MutationObserver((mutations) => {
      let shouldReinitialize = false;
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.hasAttribute && node.hasAttribute('data-certification-link')) {
              shouldReinitialize = true;
            } else if (node.querySelector && node.querySelector('[data-certification-link]')) {
              shouldReinitialize = true;
            }
          }
        });
      });
      if (shouldReinitialize) {
        this.initializeCertificationLinks();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  open(triggeredBy, certificationData) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    
    // Populate drawer with certification data
    this.populateCertificationData(certificationData);
    
    // Add animation with slight delay to ensure it triggers
    setTimeout(() => {
      this.classList.add('animate', 'active');
    }, 10);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CertificationDrawer');
        const focusElement = this.querySelector('.certification-drawer__inner') || this.querySelector('.certification-drawer__close');
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  populateCertificationData(data) {
    const logoContainer = this.querySelector('#CertificationDrawer-Logo');
    const titleElement = this.querySelector('#CertificationDrawer-Title');
    const informationElement = this.querySelector('#CertificationDrawer-Information');

    // Clear previous content
    if (logoContainer) logoContainer.innerHTML = '';
    if (titleElement) titleElement.textContent = '';
    if (informationElement) informationElement.innerHTML = '';

    // Set logo
    if (data.logo && logoContainer && data.logo !== 'null' && data.logo !== '' && data.logo !== 'undefined') {
      const img = document.createElement('img');
      // Ensure we have a valid URL
      let logoUrl = data.logo;
      
      // If it's a relative path or doesn't start with http, it might need processing
      if (logoUrl && !logoUrl.startsWith('http') && !logoUrl.startsWith('//') && !logoUrl.startsWith('/')) {
        // Try to construct full URL if it's a relative path
        logoUrl = logoUrl.startsWith('./') ? logoUrl.substring(2) : logoUrl;
      }
      
      img.src = logoUrl;
      img.alt = data.title || 'Certification logo';
      img.className = 'certification-drawer__logo-image';
      img.onerror = function() {
        // If image fails to load, hide the container
        if (logoContainer) {
          logoContainer.style.display = 'none';
        }
      };
      logoContainer.appendChild(img);
    } else if (logoContainer) {
      // Hide logo container if no logo
      logoContainer.style.display = 'none';
    }

    // Set title
    if (data.title && titleElement) {
      titleElement.textContent = data.title;
    }

    // Set information content
    if (data.more_information && informationElement) {
      // Decode HTML entities first
      const tempDiv = document.createElement('div');
      tempDiv.textContent = data.more_information;
      const decodedText = tempDiv.innerHTML;
      
      // If it contains HTML tags, insert as HTML, otherwise treat as plain text
      if (decodedText.includes('<') || data.more_information.includes('<')) {
        // Create a temporary element to decode HTML entities properly
        const temp = document.createElement('div');
        temp.innerHTML = data.more_information;
        informationElement.innerHTML = temp.innerHTML;
      } else {
        const paragraph = document.createElement('p');
        paragraph.textContent = data.more_information;
        paragraph.className = 'certification-drawer__text';
        informationElement.appendChild(paragraph);
      }
    }
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('certification-drawer', CertificationDrawer);
